import React from "react";
import { Modal, ModalHeader } from "../../globalComponents/ui/Modal";
import { PrimaryButton } from "../../globalComponents/ui/Button";
import SourceConfidenceBadge from "./SourceConfidenceBadge";
import { comparisonSeries } from "./helpers";

/**
 * Draws a KPI's trend across the analysed periods.
 *
 * @param {{values: number[], years: string[]}} props - The series and its labels.
 * @returns {JSX.Element|null} An SVG sparkline, or nothing for a single period.
 *
 * @remarks
 * Drawn rather than charted with a library: two points and a label row do not
 * justify a dependency, and inline SVG inherits the page's tokens directly.
 */
const Sparkline = ({ values, years }) => {
  if (!values || values.length < 2) return null;

  const width = 420;
  const height = 96;
  const padX = 44;
  const padY = 16;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;

  const points = values.map((value, index) => ({
    x: padX + index * ((width - 2 * padX) / (values.length - 1)),
    y: height - padY - ((value - min) / span) * (height - 2 * padY),
  }));

  return (
    <svg
      className="aica-spark"
      width={width}
      height={height + 18}
      viewBox={`0 0 ${width} ${height + 18}`}
      role="img"
      aria-label="Trend across the analysed periods"
    >
      <polyline
        points={points.map((point) => `${point.x},${point.y}`).join(" ")}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      {points.map((point, index) => (
        <circle
          key={years[index]}
          cx={point.x}
          cy={point.y}
          r="5"
          fill="var(--surface)"
          stroke="var(--accent)"
          strokeWidth="2.5"
        />
      ))}
      {points.map((point, index) => (
        <text
          key={`label-${years[index]}`}
          x={point.x}
          y={height + 12}
          textAnchor="middle"
          fontSize="10.5"
          fontWeight="600"
          fill="var(--ink-3)"
        >
          {years[index]}
        </text>
      ))}
    </svg>
  );
};

/**
 * Shows one KPI's full history, ranking and AI narrative.
 *
 * @param {object} props - The KPI, its periods, compare mode and dismissal.
 * @returns {JSX.Element|null} The modal, or nothing when no KPI is selected.
 */
const KpiDetailModal = ({ kpi, years, compareMode, onClose }) => {
  if (!kpi) return null;

  const latest = kpi.series.you.length - 1;
  const comparison = comparisonSeries(kpi.series, compareMode);
  const change = kpi.series.you[latest] - kpi.series.you[0];
  const improved = kpi.lowerIsBetter ? change < 0 : change > 0;

  return (
    <Modal show onClose={onClose} maxWidth={780}>
      <ModalHeader icon="fas fa-chart-line" title={kpi.name} subtitle={kpi.unit} onClose={onClose} />

      <div className="aica-modal-body">
        <div className="aica-value-cards">
          <div className="aica-value-card">
            <span className="aica-value-label">You</span>
            <span className="aica-value">{kpi.series.you[latest]}</span>
          </div>
          <div className="aica-value-card">
            <span className="aica-value-label">
              {compareMode === "leader" ? "Sector leader" : "Peer average"}
            </span>
            <span className="aica-value">{comparison[latest]}</span>
          </div>
          <div className="aica-value-card">
            <span className="aica-value-label">
              {kpi.lowerIsBetter ? "Lower is better" : "Higher is better"}
            </span>
            <span className="aica-value aica-value-small">
              {kpi.assessment}
            </span>
          </div>
        </div>

        <p className="aica-kpi-note">{kpi.note}</p>

        {kpi.series.you.length > 1 && (
          <>
            <div className="aica-section-label">Trend</div>
            <div
              className={`aica-change ${improved ? "is-better" : "is-worse"}`}
            >
              {change > 0 ? "+" : ""}
              {change.toFixed(2)} over {kpi.series.you.length} periods —{" "}
              {improved ? "improving" : "worsening"}
            </div>
            <Sparkline values={kpi.series.you} years={years} />
          </>
        )}

        {kpi.aiInsight && (
          <div className="aica-insight">
            <strong>AI insight</strong> {kpi.aiInsight}
          </div>
        )}

        {kpi.peerRanking?.length > 0 && (
          <>
            <div className="aica-section-label">Peer ranking</div>
            <div className="aica-table-wrap">
              <table className="aica-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Company</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {kpi.peerRanking.map((entry) => (
                    <tr key={`${entry.company}-${entry.rank}`}>
                      <td className="aica-num">{entry.rank}</td>
                      <td>{entry.company}</td>
                      <td className="aica-num">{entry.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="aica-modal-provenance">
          <SourceConfidenceBadge provenance={kpi.provenance} />
        </div>
      </div>

      <div className="aica-modal-footer">
        <PrimaryButton type="button" onClick={onClose}>
          Close
        </PrimaryButton>
      </div>
    </Modal>
  );
};

export default KpiDetailModal;
